import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/SittingArrangement.css';

interface Seat {
  seatNumber: number;
  isOccupied: boolean;
  occupant?: {
    name: string;
    studentId: string;
  };
}

const SittingArrangement: React.FC = () => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Mock seat data - in real app, this would come from API
  const totalSeats = 36;
  const seats: Seat[] = Array.from({ length: totalSeats }, (_, i) => {
    const seatNumber = i + 1;
    const isOccupied = [1, 3, 7, 12, 15, 22, 28].includes(seatNumber);
    const currentUserSeat = user?.studentId === 'STU001' ? 15 : null;
    
    return {
      seatNumber,
      isOccupied,
      occupant: isOccupied ? {
        name: seatNumber === currentUserSeat ? user?.name || 'You' : `Student ${seatNumber}`,
        studentId: seatNumber === currentUserSeat ? user?.studentId || 'STU001' : `STU${String(seatNumber).padStart(3, '0')}`
      } : undefined
    };
  });

  const currentUserSeat = seats.find(s => s.occupant?.studentId === user?.studentId);

  const handleSeatClick = (seat: Seat) => {
    if (seat.isOccupied && seat.occupant) {
      alert(`Seat ${seat.seatNumber} is occupied by ${seat.occupant.name} (${seat.occupant.studentId})`);
    } else {
      alert(`Seat ${seat.seatNumber} is available`);
    }
  };

  return (
    <div className="sitting-arrangement">
      <div className="arrangement-header">
        <div className="header-info">
          <h2>Sitting Arrangement</h2>
          <p>Total Seats: {totalSeats} | Occupied: {seats.filter(s => s.isOccupied).length} | Available: {seats.filter(s => !s.isOccupied).length}</p>
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
              <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
              <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
              <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Grid
          </button>
          <button
            className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            List
          </button>
        </div>
      </div>

      {currentUserSeat && (
        <div className="current-seat-info">
          <div className="info-icon">📍</div>
          <div className="info-content">
            <strong>Your Current Seat: {currentUserSeat.seatNumber}</strong>
            <span>Click on your seat in the arrangement to view details</span>
          </div>
        </div>
      )}

      {viewMode === 'grid' ? (
        <div className="seats-grid">
          {seats.map((seat) => {
            const isCurrentUser = seat.occupant?.studentId === user?.studentId;
            return (
              <div
                key={seat.seatNumber}
                className={`seat-card ${seat.isOccupied ? 'occupied' : 'available'} ${isCurrentUser ? 'current-user' : ''}`}
                onClick={() => handleSeatClick(seat)}
              >
                <div className="seat-number">{seat.seatNumber}</div>
                {seat.isOccupied ? (
                  <div className="seat-status">
                    {isCurrentUser ? (
                      <span className="status-badge current">You</span>
                    ) : (
                      <span className="status-badge occupied">Occupied</span>
                    )}
                  </div>
                ) : (
                  <div className="seat-status">
                    <span className="status-badge available">Available</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="seats-list">
          <table className="seats-table">
            <thead>
              <tr>
                <th>Seat Number</th>
                <th>Status</th>
                <th>Occupant</th>
                <th>Student ID</th>
              </tr>
            </thead>
            <tbody>
              {seats.map((seat) => {
                const isCurrentUser = seat.occupant?.studentId === user?.studentId;
                return (
                  <tr
                    key={seat.seatNumber}
                    className={isCurrentUser ? 'current-user-row' : ''}
                    onClick={() => handleSeatClick(seat)}
                  >
                    <td>{seat.seatNumber}</td>
                    <td>
                      {seat.isOccupied ? (
                        <span className={`status-badge ${isCurrentUser ? 'current' : 'occupied'}`}>
                          {isCurrentUser ? 'Your Seat' : 'Occupied'}
                        </span>
                      ) : (
                        <span className="status-badge available">Available</span>
                      )}
                    </td>
                    <td>{seat.occupant?.name || '-'}</td>
                    <td>{seat.occupant?.studentId || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SittingArrangement;

